import {
  call,
  delay,
  put,
  race,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import type { PayloadAction } from 'typesafe-actions';

import type {
  Automation,
  AutomationActionTypes,
  GetListedAutomationsRequestData,
  ListedAutomationsListViewPage,
} from '@payaca/types/automationTypes';
import type { SagaConfig } from './automationTypes';
import { ActionType } from './automationTypes';

import {
  archiveAutomationsFailure,
  archiveAutomationsSuccess,
  clearAutomations,
  createAutomationFailure,
  createAutomationSuccess,
  getAutomationFailure,
  getAutomationSuccess,
  getListedAutomationsPageFailure,
  getListedAutomationsPageSuccess,
  updateAutomationFailure,
  updateAutomationSuccess,
} from './automationActions';

import { Req } from '@payaca/helpers/storeHelper';
import { refreshAuthToken } from '../auth/refreshAuthToken';
import { DEFAULT_API_REQUEST_TIMEOUT_MS } from '../constants';

const automationSagaCreator = ({
  apiBaseurl,
  getAuthHeader,
  isNativeApp,
}: SagaConfig) => {
  const req = Req(`${apiBaseurl}/api`, getAuthHeader, isNativeApp);

  function* handleGetAutomation(
    action: PayloadAction<
      ActionType.REQUEST_GET_AUTOMATION,
      {
        automationId: number;
      }
    >
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(getAutomation, action.payload.automationId),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        const errorMessage = 'Get automation timed out.';
        yield put(
          getAutomationFailure(
            action.payload.automationId,
            new Error(errorMessage)
          )
        );
      } else {
        yield put(getAutomationSuccess(action.payload.automationId, response));
      }
    } catch (error: any) {
      yield put(getAutomationFailure(action.payload.automationId, error));
    }
  }

  function* handleUpdateAutomation(
    action: PayloadAction<
      ActionType.REQUEST_UPDATE_AUTOMATION,
      {
        automationId: number;
        automationUpdates: Partial<Automation<AutomationActionTypes>>;
        callback?: (automation: Automation<AutomationActionTypes>) => void;
        onError?: (error: Error) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(
          updateAutomation,
          action.payload.automationId,
          action.payload.automationUpdates
        ),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        const error = new Error('Update automation timed out.');
        yield put(updateAutomationFailure(action.payload.automationId, error));
        action.payload.onError?.(error);
      } else {
        yield put(
          updateAutomationSuccess(action.payload.automationId, response)
        );
        action.payload.callback?.(response);
      }
    } catch (error: any) {
      yield put(updateAutomationFailure(action.payload.automationId, error));
      action.payload.onError?.(error);
    }
  }

  function* handleCreateAutomation(
    action: PayloadAction<
      ActionType.REQUEST_CREATE_AUTOMATION,
      {
        callback?: (automationId: number) => void;
        onError?: (error: Error) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(createAutomation),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        const error = new Error('Create automation timed out.');
        yield put(createAutomationFailure(error));
        action.payload.onError?.(error);
      } else {
        yield put(createAutomationSuccess(response));
        action.payload.callback?.(response);
      }
    } catch (error: any) {
      yield put(createAutomationFailure(error));
      action.payload.onError?.(error);
    }
  }

  const getAutomation = async (automationId: number) => {
    const authHeader = await getAuthHeader();

    return fetch(`${apiBaseurl}/api/automations/${automationId}`, {
      method: 'GET',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        'X-Simple-Deal': 'true',
      },
    }).then(async (response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `getAutomation failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  const createAutomation = async () => {
    const authHeader = await getAuthHeader();

    return fetch(`${apiBaseurl}/api/automations`, {
      method: 'POST',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        'X-Simple-Job': 'true',
      },
      body: JSON.stringify({}),
    }).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `createAutomation failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  const updateAutomation = async (
    automationId: number,
    automationUpdates: Partial<Automation<AutomationActionTypes>>
  ) => {
    const authHeader = await getAuthHeader();

    return fetch(`${apiBaseurl}/api/automations/${automationId}`, {
      method: 'PUT',
      body: JSON.stringify(automationUpdates),
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
        'X-Simple-Deal': 'true',
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `updateAutomation failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  function* handleGetListedAutomationsPage(
    action: PayloadAction<
      ActionType.REQUEST_GET_LISTED_AUTOMATIONS_PAGE,
      {
        getListedAutomationsRequestData: GetListedAutomationsRequestData;
        callback?: (
          listedAutomationsListViewPage: ListedAutomationsListViewPage
        ) => void;
      }
    >
  ) {
    yield call(refreshAuthToken);

    try {
      const { response, timeout } = yield race({
        response: call(
          getListedAutomationsPage,
          action.payload.getListedAutomationsRequestData
        ),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          getListedAutomationsPageFailure(
            new Error('Get listed automations request timed out.')
          )
        );
      } else {
        yield put(getListedAutomationsPageSuccess(response));
        action.payload.callback?.(response);
      }
    } catch (error: any) {
      yield put(getListedAutomationsPageFailure(error));
    }
  }

  const getListedAutomationsPage = async (
    getListedAutomationsRequestData: GetListedAutomationsRequestData
  ) => {
    return fetch(
      `${apiBaseurl}/api/automations?pageNumber=${
        getListedAutomationsRequestData.pageNumber
      }&pageSize=${getListedAutomationsRequestData.pageSize}&searchTerm=${
        getListedAutomationsRequestData.searchTerm || ''
      }&sortBy=${getListedAutomationsRequestData.sortBy || ''}&sortDirection=${
        getListedAutomationsRequestData.sortDirection || ''
      }&entityTypes=${
        getListedAutomationsRequestData.entityTypes
          ? getListedAutomationsRequestData.entityTypes.join(',')
          : ''
      }&triggerEvents=${
        getListedAutomationsRequestData.triggerEvents
          ? getListedAutomationsRequestData.triggerEvents.join(',')
          : ''
      }&actionTypes=${
        getListedAutomationsRequestData.actionTypes
          ? getListedAutomationsRequestData.actionTypes.join(',')
          : ''
      }`,
      {
        method: 'GET',
        headers: {
          Authorization: await getAuthHeader(),
          'Content-Type': 'application/json',
          'X-Simple-Job': 'true',
        },
      }
    ).then((response) => {
      if (response.ok) {
        return response.json();
      } else {
        throw new Error(
          `GetListedAutomationsPage failed: ${response.status} ${response.statusText}`
        );
      }
    });
  };

  function* handleArchiveAutomations(
    action: PayloadAction<
      ActionType.REQUEST_ARCHIVE_AUTOMATIONS,
      {
        automationIds: number[];
        onArchiveSuccess?: () => void;
      }
    >
  ) {
    yield call(refreshAuthToken);

    try {
      const { response, timeout } = yield race({
        response: call(archiveAutomations, action.payload.automationIds),
        timeout: delay(DEFAULT_API_REQUEST_TIMEOUT_MS),
      });

      if (timeout) {
        yield put(
          archiveAutomationsFailure(new Error('Archive automations timed out.'))
        );
      } else {
        // oxlint-disable-next-line eslint/no-unused-expressions -- FIXME: pre-existing error: Expected expression to be used
        action.payload.onArchiveSuccess && action.payload.onArchiveSuccess();
        yield put(archiveAutomationsSuccess());
      }
    } catch (error) {
      archiveAutomationsFailure(new Error('Archive automations timed out.'));
    }
  }

  const archiveAutomations = async (automationIds: number[]) => {
    const response = await req.put('/automations/bulk_archive', {
      automationIds,
    });
    return response;
  };

  function* handleAppLogout() {
    yield put(clearAutomations());
  }

  return function* () {
    yield takeLatest(ActionType.REQUEST_GET_AUTOMATION, handleGetAutomation);
    yield takeLatest(
      ActionType.REQUEST_UPDATE_AUTOMATION,
      handleUpdateAutomation
    );
    yield takeLatest(
      ActionType.REQUEST_CREATE_AUTOMATION,
      handleCreateAutomation
    );
    yield takeLatest(
      ActionType.REQUEST_GET_LISTED_AUTOMATIONS_PAGE,
      handleGetListedAutomationsPage
    );
    yield takeLatest(
      ActionType.REQUEST_ARCHIVE_AUTOMATIONS,
      handleArchiveAutomations
    );
    yield takeEvery('auth.logout', handleAppLogout);
  };
};

export default automationSagaCreator;
